import React, { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FiTrendingUp } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from '@core/types/errorResponse';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { Activity } from "@features/admin/activity/types/activity";
import type { AttachedKpi, Kpi } from "@features/admin/kpi/types/kpi";
import { fetchKpiAsync, fetchAssignedKpiAsync } from "@features/admin/kpi/services/kpiSvc";
import { useKpiOptions } from "@features/admin/kpi/hooks/useKpiOptions";
import { useActivityAction } from "../hooks/useActivityAction";
import { removeActivityKpiAsync } from "../services/activitySvc";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { SORT_BY_KPI_NAME, SORT_DIR_ASC } from "@core/constants/listSort";
import { useScopeId } from "@core/hooks/useRouteParamId";
import AssignModalShell from "@core/components/assign/AssignModalShell";
import AssignDualListPanels from "@core/components/assign/AssignDualListPanels";
import AssignAvailableRow from "@core/components/assign/AssignAvailableRow";
import AssignAssignedRow from "@core/components/assign/AssignAssignedRow";
import {
    buildActiveLookup,
    filterActiveForAssignPayload,
    resolveAssignedRowDisplay,
    sortAttachedByOrder,
} from "@core/components/assign/assignDisplay";
import { renumber, setItemOrder } from "@core/components/assign/assignListOrder";
import { todayDateOnly, toDateOnlyInputValue } from "@core/utils/formatDateOnly";
import { buildFormulaPreview, ENTRY_MODE_CALCULATED } from "@features/admin/kpi/types/kpiEntryMode";
import { handleAssignFailureAfterPartialRemove } from "@core/components/assign/assignPartialSaveError";
import {
    selectGlobalErrorOpen,
    useErrorModalStore,
} from "@infrastructure/api/store/errorModalStore";

type Props = {
    show: boolean;
    onHide: () => void;
    activity: Activity | null;
};

type AttachedKpiRow = AttachedKpi & {
    kpi_name?: string;
    unit_id?: number | null;
    kpi_is_active?: boolean;
};

const AssignActivityKPIModal: React.FC<Props> = ({ show, onHide, activity }) => {
    const [allKpis, setAllKpis] = useState<Kpi[]>([]);
    const [attached, setAttached] = useState<AttachedKpiRow[]>([]);
    const [initialAttachedIds, setInitialAttachedIds] = useState<number[]>([]);
    const attachedIds = useMemo(() => attached.map(x => x.kpi_id), [attached]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const [qLeft, setQLeft] = useState("");
    const [qRight, setQRight] = useState("");

    const showError = useApiError();
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const globalErrorOpen = useErrorModalStore(selectGlobalErrorOpen);

    const resolvedNationId = useScopeId() ?? 0;

    const { kpiUnits, fetchKpiUnits } = useKpiOptions();

    const { doActivityAssignKPI } = useActivityAction();

    const activeKpiIds = useMemo(
        () => new Set(allKpis.map((k) => k.kpi_id)),
        [allKpis]
    );
    const activeKpiLookup = useMemo(
        () =>
            buildActiveLookup(
                allKpis,
                (k) => k.kpi_id,
                (k) => k.kpi_name,
                (k) => `Unit: ${kpiUnits?.find((u) => u.unit_id === k.unit_id)?.unit_name ?? "—"}`
            ),
        [allKpis, kpiUnits]
    );
    const explicitKpiActiveById = useMemo(() => {
        const map = new Map<number, boolean>();
        for (const row of attached) {
            if (row.kpi_is_active !== undefined) {
                map.set(row.kpi_id, row.kpi_is_active);
            }
        }
        return map;
    }, [attached]);

    useEffect(() => {
        fetchKpiUnits();
    }, [fetchKpiUnits]);

    const reloadAssignedFromServer = useCallback(async () => {
        if (!activity?.activity_id) return;

        const assignedItems = await fetchAllPages<any>((page) =>
            fetchAssignedKpiAsync(resolvedNationId, activity.activity_id, { all: true, page })
        );

        const normalized = renumber(
            assignedItems.map((k: any, idx: number) => ({
                kpi_id: Number(k.kpi_id),
                is_required: Boolean(k.is_required ?? false),
                required_at: k.required_at ?? null,
                list_order: Number(k.list_order ?? idx + 1),
                kpi_name: k.kpi_name,
                unit_id: k.unit_id ?? null,
                kpi_is_active:
                    k.kpi_is_active !== undefined
                        ? Boolean(k.kpi_is_active)
                        : k.is_active !== undefined
                          ? Boolean(k.is_active)
                          : undefined,
            }))
        );

        setAttached(normalized);
        setInitialAttachedIds(normalized.map((x) => x.kpi_id));
    }, [activity?.activity_id, resolvedNationId]);

    useEffect(() => {
        if (!show || !activity?.activity_id) return;

        let cancelled = false;

        (async () => {
            setLoading(true);

            try {
                const allItems = await fetchAllPages<Kpi>((page) =>
                    fetchKpiAsync(resolvedNationId, {
                        all: true,
                        page,
                        isActive: true,
                        sortBy: SORT_BY_KPI_NAME,
                        sortDir: SORT_DIR_ASC,
                    })
                );
                if (cancelled) return;

                const seen = new Set<number>();
                const uniqueItems = allItems.filter(k => {
                    if (seen.has(k.kpi_id)) return false;
                    seen.add(k.kpi_id);
                    return true;
                });
                setAllKpis(uniqueItems);

                await reloadAssignedFromServer();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [show, activity?.activity_id, resolvedNationId, reloadAssignedFromServer]);

    const left = useMemo(() => {
        const base = allKpis.filter(k => !attachedIds.includes(k.kpi_id));

        if (!qLeft.trim()) return base;

        const s = qLeft.toLowerCase();

        return base.filter(k =>
            k.kpi_name.toLowerCase().includes(s) || String(k.unit_id ?? "").includes(s)
        );
    }, [allKpis, attachedIds, qLeft]);

    const visibleAttached = useMemo(() => {
        const ordered = sortAttachedByOrder(attached);

        if (!qRight.trim()) return ordered;

        const s = qRight.toLowerCase();

        return ordered.filter((row) => {
            const display = resolveAssignedRowDisplay(
                row.kpi_id,
                activeKpiIds,
                activeKpiLookup,
                {
                    title: row.kpi_name ?? `KPI #${row.kpi_id}`,
                    sublabel: `Unit: ${kpiUnits?.find((u) => u.unit_id === row.unit_id)?.unit_name ?? "—"}`,
                },
                row.kpi_is_active
            );
            return (
                display.title.toLowerCase().includes(s) ||
                (display.sublabel?.toLowerCase().includes(s) ?? false)
            );
        });
    }, [attached, qRight, activeKpiIds, activeKpiLookup, kpiUnits]);

    const attach = (id: number) => {
        const kpi = allKpis.find((k) => k.kpi_id === id);
        setAttached(prev => {
            if (prev.some(x => x.kpi_id === id)) return prev;
            return renumber([
                ...prev,
                {
                    kpi_id: id,
                    is_required: false,
                    required_at: null,
                    list_order: prev.length + 1,
                    kpi_name: kpi?.kpi_name,
                    unit_id: kpi?.unit_id ?? null,
                    kpi_is_active: true,
                },
            ]);
        });
    };

    const detach = (id: number) => {
        setAttached(prev => renumber(prev.filter(x => x.kpi_id !== id)));
    };

    const handleOrderChange = (id: number, newOrder: number) => {
        setAttached(prev => setItemOrder(prev, id, newOrder, (x) => x.kpi_id));
    };

    const setRequired = (id: number, required: boolean) => {
        setAttached(prev =>
            prev.map(x =>
                x.kpi_id === id
                    ? {
                          ...x,
                          is_required: required,
                          required_at: required ? (x.required_at || todayDateOnly()) : null,
                      }
                    : x
            )
        );
    };

    const setRequiredAt = (id: number, requiredAt: string) => {
        setAttached(prev =>
            prev.map(x => (x.kpi_id === id ? { ...x, required_at: requiredAt || null } : x))
        );
    };

    const handleClose = () => {
        setQLeft("");
        setQRight("");
        onHide();
    };

    const finishAssignAfterRemove = async (removeSucceededThisSave: boolean) => {
        if (!activity?.activity_id) return;
        const activeAttached = filterActiveForAssignPayload(
            attached,
            activeKpiIds,
            (x) => x.kpi_id,
            explicitKpiActiveById
        );

        if (activeAttached.length > 0) {
            const payload = {
                kpis: renumber(activeAttached).map(x => ({
                    kpi_id: x.kpi_id,
                    is_required: x.is_required,
                    required_at: x.required_at,
                    list_order: x.list_order,
                })),
            };

            const response = await doActivityAssignKPI(
                resolvedNationId,
                activity.activity_id,
                payload
            );
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "KPIs updated successfully.");
                onHide();
            } else {
                const assignError = response.getError() as ErrorResponse;
                await handleAssignFailureAfterPartialRemove(
                    removeSucceededThisSave,
                    reloadAssignedFromServer,
                    assignError,
                    showError
                );
            }
            return;
        }

        showSuccess("KPIs updated successfully.");
        onHide();
    };

    const handleSave = async () => {
        if (!activity?.activity_id) {
            return;
        }

        setSaving(true);
        let removeSucceededThisSave = false;

        try {
            const currentIds = new Set(attached.map(x => x.kpi_id));
            const removedIds = initialAttachedIds.filter(id => !currentIds.has(id));

            if (removedIds.length > 0) {
                const removeResponse = await removeActivityKpiAsync(
                    resolvedNationId,
                    activity.activity_id,
                    { kpi_ids: removedIds }
                );

                if (!removeResponse.isSuccess()) {
                    showError(removeResponse.getError() as ErrorResponse);
                    return;
                }
                removeSucceededThisSave = true;
            }

            await finishAssignAfterRemove(removeSucceededThisSave);
        } catch (error) {
            showError(error as ErrorResponse);
        } finally {
            setSaving(false);
        }
    };

    const getUnitLabel = (kpi: Kpi) =>
        `Unit: ${kpiUnits?.find((u) => u.unit_id === kpi.unit_id)?.unit_name ?? "—"}`;

    const getAssignedSublabel = (row: AttachedKpiRow) => {
        const fromCatalog = allKpis.find((k) => k.kpi_id === row.kpi_id);
        const entryMode = (fromCatalog?.entry_mode ?? "").toUpperCase();
        if (entryMode === ENTRY_MODE_CALCULATED) {
            const preview = buildFormulaPreview(
                fromCatalog?.operation,
                fromCatalog?.operands ?? []
            );
            if (preview) return `Calculated: ${preview}`;
            return "Calculated";
        }
        return getUnitLabel(fromCatalog ?? ({ unit_id: row.unit_id } as Kpi));
    };
    return (
        <>
        <AssignModalShell
            show={show}
            onHide={handleClose}
            title={
                <>
                    <FiTrendingUp />
                    <span data-testid="assign-kpi-title">
                        Manage KPIs{activity ? `: ${activity.activity_name}` : ""}
                    </span>
                </>
            }
            saving={saving}
            keyboard={!globalErrorOpen}
            saveButtonRef={saveButtonRef}
            onSave={() => { void handleSave(); }}
            onCancel={handleClose}
            testId="assign-kpi-modal"
            saveTestId="save-btn"
        >
            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" data-testid="assign-kpi-loading" />
                </div>
            ) : (
                <AssignDualListPanels
                    availableTitle="Available KPIs"
                    assignedTitle="Assigned to Activity"
                    availableCount={left.length}
                    assignedCount={attached.length}
                    availableSearchPlaceholder="Search KPIs"
                    assignedSearchPlaceholder="Search assigned KPIs"
                    availableSearch={qLeft}
                    assignedSearch={qRight}
                    onAvailableSearchChange={setQLeft}
                    onAssignedSearchChange={setQRight}
                    availableSearchTestId="search-available-kpis"
                    assignedSearchTestId="search-assigned-kpis"
                    availableListTestId="available-kpi-list"
                    assignedListTestId="assigned-kpi-list"
                    availableEmptyText="No more KPIs to add."
                    assignedEmptyText="No KPIs assigned yet."
                    availableIsEmpty={left.length === 0}
                    assignedIsEmpty={visibleAttached.length === 0}
                    availableItems={left.map((k) =>
                        createElement(AssignAvailableRow, {
                            key: k.kpi_id,
                            title: k.kpi_name,
                            sublabel: getUnitLabel(k),
                            onAdd: () => attach(k.kpi_id),
                            addTestId: `add-kpi-${k.kpi_id}`,
                        })
                    )}
                    assignedItems={visibleAttached.map((row) => {
                        const display = resolveAssignedRowDisplay(
                            row.kpi_id,
                            activeKpiIds,
                            activeKpiLookup,
                            {
                                title: row.kpi_name ?? `KPI #${row.kpi_id}`,
                                sublabel: `Unit: ${kpiUnits?.find((u) => u.unit_id === row.unit_id)?.unit_name ?? "—"}`,
                            },
                            row.kpi_is_active
                        );

                        return createElement(AssignAssignedRow, {
                            key: row.kpi_id,
                            title: display.title,
                            sublabel: getAssignedSublabel(row),
                            isActive: display.isActive,
                            listOrder: row.list_order,
                            listLength: visibleAttached.length,
                            onOrderChange: (newOrder: number) => handleOrderChange(row.kpi_id, newOrder),
                            onRemove: () => detach(row.kpi_id),
                            showRequiredFields: true,
                            isRequired: row.is_required,
                            onRequiredChange: (required: boolean) => setRequired(row.kpi_id, required),
                            requiredAt: toDateOnlyInputValue(row.required_at),
                            onRequiredAtChange: (value: string) => setRequiredAt(row.kpi_id, value),
                            requiredSwitchId: `required-${row.kpi_id}`,
                        });
                    })}
                />
            )}
        </AssignModalShell>
        </>
    );
};

export default AssignActivityKPIModal;
